/**
 * LBR2-D2 careers-page — apply form (embedded in the opening detail page).
 *
 * Works both as a logged-in candidate (token attached by careersApi.applyToOpening)
 * and as a guest (token optional). Resume uploaded as multipart. On success it
 * records the application in device-local history and shows a confirmation card.
 */
import { useRef, useState } from 'react';
import { CheckCircle, AlertCircle, UploadCloud, X } from 'lucide-react';
import { validateApplyForm, type ApplyFormInput } from './applyValidation';
import { applyToOpening, recordApplication } from './careersApi';

const inputCls =
  'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#f5870a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f5870a]/20';

type Stage = 'form' | 'submitting' | 'done';

export function ApplyForm({ jobOpeningId, jobTitle }: { jobOpeningId: string; jobTitle: string }) {
  const [f, setF] = useState<ApplyFormInput>({ applicantName: '', phone: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof ApplyFormInput, string>>>({});
  const [formErr, setFormErr] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>('form');
  const fileRef = useRef<HTMLInputElement>(null);

  const set =
    (k: keyof ApplyFormInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setF((p) => ({ ...p, [k]: e.target.value }));

  const pickResume = (file: File | null) => {
    setFormErr('');
    if (!file) {
      setResume(null);
      return;
    }
    const okName = /\.(pdf|docx?)$/i.test(file.name);
    const okType = /(pdf|msword|officedocument)/i.test(file.type || '');
    if (!okName && !okType) {
      setFormErr('Resume must be a PDF or Word document');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setFormErr('Resume must be under 10 MB');
      return;
    }
    setResume(file);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validateApplyForm(f);
    setErrors(v.errors);
    if (!v.ok) return;
    setFormErr('');
    setStage('submitting');
    try {
      const r = await applyToOpening({ ...f, jobOpeningId }, resume);
      recordApplication({
        applicationId: r.applicationId,
        jobOpeningId,
        jobTitle,
        appliedAt: new Date().toISOString(),
      });
      setStage('done');
    } catch (err: any) {
      setFormErr(err.message || 'Could not submit your application. Please try again.');
      setStage('form');
    }
  };

  if (stage === 'done') {
    return (
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6 text-center">
        <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-600" />
        <h3 className="font-display text-xl font-bold text-gray-900">Application submitted!</h3>
        <p className="mt-1 text-sm text-gray-600">
          Thanks for applying to <strong>{jobTitle}</strong>. Our team will review your profile and
          reach out.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
    >
      <h3 className="font-display text-lg font-bold text-gray-900">Apply for this role</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Full name *</label>
          <input className={inputCls} value={f.applicantName} onChange={set('applicantName')} placeholder="Your name" />
          {errors.applicantName && <p className="mt-1 text-xs text-red-600">{errors.applicantName}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Phone *</label>
          <input className={inputCls} value={f.phone} onChange={set('phone')} placeholder="10-digit mobile" inputMode="tel" />
          {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Email</label>
          <input className={inputCls} value={f.email || ''} onChange={set('email')} placeholder="you@example.com" inputMode="email" />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Current location</label>
          <input className={inputCls} value={f.currentLocation || ''} onChange={set('currentLocation')} placeholder="City" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Experience (yrs)</label>
          <input className={inputCls} value={f.experienceYears || ''} onChange={set('experienceYears')} placeholder="e.g. 3" inputMode="decimal" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Notice period (days)</label>
          <input className={inputCls} value={f.noticePeriodDays || ''} onChange={set('noticePeriodDays')} placeholder="e.g. 30" inputMode="numeric" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Current CTC (₹ lakh)</label>
          <input className={inputCls} value={f.currentCtcLakh || ''} onChange={set('currentCtcLakh')} placeholder="e.g. 6" inputMode="decimal" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-semibold text-gray-700">Expected CTC (₹ lakh)</label>
          <input className={inputCls} value={f.expectedCtcLakh || ''} onChange={set('expectedCtcLakh')} placeholder="e.g. 8" inputMode="decimal" />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-semibold text-gray-700">Notes</label>
        <textarea className={inputCls} rows={3} value={f.notes || ''} onChange={set('notes')} placeholder="Anything you'd like us to know" />
      </div>
      <div>
        <label className="mb-1 block text-sm font-semibold text-gray-700">
          Resume <span className="font-normal text-gray-400">(PDF or Word, ≤10 MB)</span>
        </label>
        {resume ? (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-3">
            <span className="truncate text-sm text-gray-700">{resume.name}</span>
            <button
              type="button"
              onClick={() => {
                setResume(null);
                if (fileRef.current) fileRef.current.value = '';
              }}
              className="rounded-full p-1 hover:bg-gray-100"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileRef.current?.click()}
            className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 py-6 text-gray-400 hover:border-[#f5870a] hover:bg-orange-50"
          >
            <UploadCloud className="h-5 w-5" />
            <span className="text-sm font-medium text-gray-600">Tap to upload resume</span>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => pickResume(e.target.files?.[0] || null)}
        />
      </div>
      {formErr && (
        <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {formErr}
        </div>
      )}
      <button
        type="submit"
        disabled={stage === 'submitting'}
        className="w-full rounded-xl bg-[#f5870a] px-4 py-3 text-sm font-bold text-white shadow hover:bg-[#e07809] disabled:opacity-60"
      >
        {stage === 'submitting' ? 'Submitting…' : 'Submit application'}
      </button>
    </form>
  );
}

export default ApplyForm;
